import React, { useState, useEffect, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import './Menu.css';

const calculateWidth = (text, boxed = false, fontSize = 17) => {
  const tempDiv = document.createElement('div');
  document.body.appendChild(tempDiv);
  tempDiv.style.fontSize = `${fontSize}px`;
  tempDiv.style.position = 'absolute';
  tempDiv.style.left = '-1000px';
  tempDiv.style.top = '-1000px';
  tempDiv.style.whiteSpace = 'nowrap';
  tempDiv.style.lineHeight = '32px';
  tempDiv.style.fontFamily = "'Source Sans Pro', sans-serif";
  tempDiv.innerHTML = text;
  // room for padding value
  const width = 30 + tempDiv.clientWidth + (boxed ? 17 : 11);
  document.body.removeChild(tempDiv);
  return width;
};

const Menu = ({
  id = '',
  className = '',
  label = 'Select',
  hasLabel = false,
  options = [],
  selectedLabel,
  onChange,
  boxed = false,
  placeholder = 'No Options',
  labelledBy,
  ...otherProps
}) => {
  const selectRef = useRef(null);
  
  // Initialize state
  const [selectedOpt, setSelectedOpt] = useState(() => {
    if (options.length === 0) return '';
    
    if (selectedLabel) {
      const foundOption = options.find(opt => opt.name === selectedLabel);
      return foundOption ? foundOption.id : options[0].id;
    }
    
    return options[0].id;
  });

  const [currentLabel, setCurrentLabel] = useState(() => {
    if (options.length === 0) return 'No options';
    
    if (selectedLabel) {
      const foundOption = options.find(opt => opt.name === selectedLabel);
      return foundOption ? foundOption.name : options[0].name;
    }
    
    return options[0].name;
  });

  // Update width when component mounts or selection changes
  useEffect(() => {
    if (selectRef.current && currentLabel) {
      selectRef.current.style.width = `${calculateWidth(currentLabel, boxed, 20)}px`;
    }
  }, [currentLabel, boxed]);

  // Handle prop changes
  useEffect(() => {
    if (selectedLabel && options.length > 0) {
      const foundOption = options.find(opt => opt.name === selectedLabel);
      if (foundOption && foundOption.id !== selectedOpt) {
        setSelectedOpt(foundOption.id);
        setCurrentLabel(foundOption.name);
      }
    }
  }, [selectedLabel, options, selectedOpt]);

  // Handle selection change
  const handleSelection = useCallback((event) => {
    event.preventDefault();
    event.stopPropagation();
    
    const newValue = event.target.value;
    const selectedOption = options.find(opt => `${opt.id}` === newValue);
    
    if (selectedOption) {
      setSelectedOpt(newValue);
      setCurrentLabel(selectedOption.name);
      
      if (onChange) {
        const index = options.findIndex(opt => `${opt.id}` === newValue);
        onChange(index);
      }
    }
  }, [options, onChange]);

  const containerClasses = classNames(
    'ng-ui-widgets',
    'ng-ui-menu',
    className
  );

  const selectClasses = classNames(
    'mlSelect',
    { 'boxed': boxed }
  );

  const labelClasses = classNames(
    'ng-ui-menu-label',
    { 'visually-hidden': !hasLabel }
  );

  const uniqueId = id ? `ng_menu_${id}` : `ng_menu_${Date.now()}`;

  return (
    <div {...otherProps} className={containerClasses}>
      <label htmlFor={uniqueId} className={labelClasses}>
        {label}
      </label>
      <select
        id={uniqueId}
        ref={selectRef}
        className={selectClasses}
        aria-labelledby={labelledBy}
        value={selectedOpt}
        onChange={handleSelection}
      >
        {selectedOpt === '' && (
          <option disabled value="">
            {placeholder}
          </option>
        )}
        {options.map(opt => (
          <option 
            key={opt.id} 
            value={opt.id} 
            className="options"
          >
            {opt.name}
          </option>
        ))}
      </select>
    </div>
  );
};

Menu.propTypes = {
  id: PropTypes.string,
  className: PropTypes.string,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      name: PropTypes.string.isRequired,
    })
  ),
  selectedLabel: PropTypes.string,
  label: PropTypes.string,
  hasLabel: PropTypes.bool,
  onChange: PropTypes.func,
  boxed: PropTypes.bool,
  placeholder: PropTypes.string,
  labelledBy: PropTypes.string,
};

export default Menu;